//	+-----------------------------------------------+
//	| DfPE view objects                             |
//	+-----------------------------------------------+

/**
 * @file cardboard/baseViews.js
 * @desc DfPE view objects: This file contains the view base class
 * and other important view objects.
 * @author Geoffrey Pursell
 */

/* global settings */

define('cardboard/baseViews',[
    // libraries
    "jquery", "underscore", "backbone", "hogan", "modernizr", "jquery.easing", "bowser",

    // app modules
    "cardboard/util", "cardboard/pluginHelper",

    // templates
    "text!templates/reportList.html!strip", "text!templates/dataBoxAdder.html!strip"
],
       function ($, _, Backbone, Hogan, Modernizr, jQueryEasing, Bowser,
                  Util, PluginHelper,
                  reportListTemplate, dataBoxAdderTemplate) {

    console.log("MODULE: cardboard/baseViews");

    /**
	 * @exports cardboard/baseViews
	 * @desc DfPE view objects: This module contains the view base class
	 * and other important view objects.
	 */
    var BaseViews = {};

    /**
	 * Base class to provide a progress bar until data fetch, and other stuff
	 * @class module:cardboard/baseViews.BaseView
	 * @classdesc Our base View object type, based on Backbone.View
	 * @augments Backbone.View
	 */
    BaseViews.BaseView = Backbone.View.extend(/** @lends module:cardboard/baseViews.BaseView.prototype */ {

        /** if there's a dataSetPath, expand that into default dataPath and
		* metaDataPath attributes -- but if either of those are explicity given
		* also, let that take precedence
		 * @param {Object} viewInfo - the viewInfo from the plugin JSON
		 * @returns {Object} the altered viewInfo object */
        preProcessViewInfo: function (viewInfo) {

            if (viewInfo.dataSetPath) {
                var generatedValues = {
                    dataPath: viewInfo.dataSetPath + ".data",
                    metaDataPath: viewInfo.dataSetPath + ".meta"
                };
                viewInfo = _.defaults(viewInfo, generatedValues);
            }

            return viewInfo;
        },

        /** render the view, must be implemented by derived objects */
        render: function () {
            console.log("render not implemented yet");
        },

        /** default destructor */
        destroy: function () {
            this.undelegateEvents();
            this.stopListening();
            if (this.$el) { this.$el.remove(); }
        },

        /** shows some in-progress UI so the user knows we're not dead
		 * @param {jQuery | Element | String} element an optional
		 * sub-element into which the progress thingy should go */
        showProgress: function (element) {
            var $e = _.isUndefined(element) ? this.$el : this.$(element);
            // this is intended to only show the progress bar if the
            // underlying model hasn't been loaded yet
            if (this.model && !this.model.isLoaded()) {
                $e.html("<div class='viewPlaceholder'>Loading...</div>");
            }
        },
        /** mask the view with a spinner to indicate that its contents are loading */
        maskForLoading: function (useSpinner) {
            Util.UI.maskForLoading(this.el, useSpinner);
        },
        /** unmask the view to indicate that loading is complete */
        unmaskForLoading: function () {
            Util.UI.unmaskForLoading(this.el);
        },

        /** attach declarative global events */
        attachGlobalEvents: function () {
            _(this.globalEvents).each(_.bind(function (value, key) {
                Backbone.on(key, this[value], this);
            }, this));
        },

        /** TODO: optimize this, and figure out why the .pick() step needs to
		 * happen. Can/should this be replaced with
		 * Util.Data.flattenModelsAndCollections()? Which is faster? */
        flattenModels: function (dataStructure) {
            return _(dataStructure)
                .map(function (source, name) {
                return [name, source.toJSON()];
            })
                .object()
                .pick(this.options.viewInfo.dataSourceName)
                .value();
        },

        /** adds basically all the data we have to an environment and returns
		 * it. this is useful for feeding into pane toggle header templates so
		 * we can make decisions about the org, render the current and/or
		 * previous year(s), etc.  NOTE: should this really be at the base class
		 * level? 
		 * @returns {Object} basically every piece of data we have. */
        getDataEnvironment: function () {
            var dataBoxView = this.options.dataBoxView,
                environment = { org: dataBoxView.getOrg() };

            if (dataBoxView && dataBoxView.controller.dataSources) {
                // this transform is needed because each data source is a
                // Backbone model/collection, but we need bare JS objects to
                // feed into mustache templates.
                environment.dataSources = this.flattenModels(dataBoxView.controller.dataSources);
            }

            return environment;
        },

        // Get text content from window.dynamicTextContent collection.
        // Accepts a sting path for site content. Assumes <modelname>.<attribute>.<index>(if array)
    /*    getDynamicContent: function(path){
            try{ 
                var pathParts = path.split('.');
                var dataContext = window.dynamicTextCollection.findWhere({"area": pathParts[0]}).get(pathParts[1])
                // This gets messy. Need to support "contents" as an array or object, and named keys or interators
                if( _.isArray(dataContext) ){
                    //Check for a bad path
                    if(_.isNaN(parseInt(pathParts[2]))){
                        throw "Bad titlePath: Check for non integer."
                    }
                    return dataContext[pathParts[2]];
                } else if(_.isObject(dataContext)) {
                    if(typeof pathParts[2] == "string"){
                        // if highSchoolAcademics.contents."1" (string)
                        var numberTest = parseInt(pathParts[2]);
                        //if highSchoolAcademics.contents.thing4
                        if(_.isNaN(numberTest)){
                            return dataContext[pathParts[2]];
                        } else {
                            return dataContext[numberTest];
                        }
                    }
                    return dataContext.pathParts[2];
                } else {
                    return dataContext
                }
            } catch(e) {
                console.log("ERROR GETTING DYNAMIC CONTENT: "+path, e);
                return path;
            }
        },*/

        // Leave a data- attr on an element for Cardstock to track content locations
        /*  setDynamicContentDataString: function(path, $el) {
            $el.attr('data-dynamickey', path);
        },*/

        /** show or hide this view, based on any conditions we may
		 * find in its viewInfo. Handles the following options:
		 * Also handle the visibleTo attribute, which can
		 * make views visible to screenreaders only. Usually, this is called by
		 * an event handler that gets set up in a view's initialize() or
		 * render().  NOTE: I think this is kind of expensive to run because of
		 * the call to getDataEnvironment(). Use with caution. */
        applyVisibility: function () {
            var self = this,
                vi = this.options.viewInfo,
                $existingPlaceholder = this.$el.next(".viewPlaceholder"),
                activeCondition,
                conditions = [];

            // we process these in this order: first in the list has higher precedence
            if (vi.visibleTo) { conditions.push(this._processVisibleTo); }
            if (vi.errorConditions) {conditions.push(this._processErrorConditions); }
            if (vi.visibleWhen) { conditions.push(this._processVisibleWhen); }

            // we want to "keep" the first one that makes it invisible, so we
            // can reference its message.  all views are presumed visible until
            // proven otherwise.
            activeCondition = _(conditions).reduce(function (memo, conditionFunction) {
                if (memo !== null) { return memo; }
                var result = conditionFunction.call(self, vi);
                if (result.visible === false) { return result; }
                return null;
            }, null);

            // take action: show or hide the view
            if (activeCondition && activeCondition.visible === false) {
                this.$el.addClass("hiddenOffscreen");

                // if there's a "not visible" message provided by the view
                // config, let's show that, unless there's not-yet-loaded data;
                // we don't want to flash an error message during loading.
                if (!(this.model && !this.model.loaded) && activeCondition.notVisibleMessage) {
                    $existingPlaceholder.remove();
                    this.$el.after("<div class='viewPlaceholder'>" + activeCondition.notVisibleMessage + "</div>");
                }

            } else {
                this.$el.removeClass("hiddenOffscreen");
                // if there's "not visible" placeholder right after the view,
                // remove it.
                $existingPlaceholder.remove();
            }
        },
        /** helper method for _processErrorConditions().
		 * @private
		 * @param {Object} viewInfo - the viewInfo from the plugin JSON */
        _processVisibleTo: function (viewInfo) {
            return {
                visible: (viewInfo.visibleTo !== "screenreadersOnly")
            };
        },
        /** helper method for _processErrorConditions().
		 * @private
		 * @param {Object} viewInfo - the viewInfo from the plugin JSON */
        _processVisibleWhen: function (viewInfo) {
            var environment = this.getDataEnvironment(),
                notVisibleMessage = viewInfo.notVisibleMessage ||
                Util.Data.getDataFromPath(environment, viewInfo.notVisibleMessagePath);

            return {
                visible: Util.evaluateFilterCondition(viewInfo.visibleWhen, environment),
                notVisibleMessage: notVisibleMessage
            };
        },
        /** responsible for
		 * @private
		 * @param {Object} viewInfo - the viewInfo from the plugin JSON */
        _processErrorConditions: function (viewInfo) {
 
            var errors = [], errorMessages = [],
                environment = {
                    meta: this.model.getCleanMetaDataView(viewInfo),
                    data: this.model.getCleanDataView(viewInfo)
                };
      
            _(viewInfo.errorConditions).each(function (errorCondition) {
                var isError = Util.evaluateFilterCondition(errorCondition, environment);
                if (isError) { errors.push(errorCondition.error); }
            });

            _(errors).each(function (error) {
                var message;
                if (error.messagePath) {
                    message = Util.Data.getDataFromPath(environment, error.messagePath);
                    if (message === undefined) {
                        console.log(viewInfo);
                        console.log(error);
                        console.log(environment);
                        console.log("No error condition message found at path " + error.messagePath);
                        message = "<div class='msg_dataIsNot'>Data is not available for current selection(s).</div>";
                    }
                } else {
                    message = error.message;
                }
                errorMessages.push(message);
            });

            return {
                visible: !(errors.length),
                notVisibleMessage: errorMessages.join("; ")
            };
        },

        /** make all events that get fired on the given view also be
		 * fired on this view. good for allowing events on nested view
		 * to "propagate" upwards. */
        repeatEventsFrom: function (otherView) {
            otherView.on("all", function (eventName, info) {
                this.trigger(eventName, info);
            }, this);
        },

        /** create and set up the view's wrapper element, into which
		 * it will render itself.
		 * @param {Object} options the options hash passed to the
		 * view's constructor. At least some of these options should
		 * map to variable names in the view's template string.
		 * @returns {jQuery} the newly created element */
        createWrapperElement: function (viewModel) {
            var t = this.wrapperTemplate;

            // WAIT! We might have to compile the template...
            if (typeof t === "string") {
                t = Hogan.compile(this.wrapperTemplate);
            }

            var elString = t.render(viewModel || {}),
                $el = $(elString);

            // set the view to properly consider itself as attached to the piece
            // of DOM we just created, unless there already is one.
            this.setElement($el);

            return $el;
        },

        //
        // Info functions & accessors
        //

        /** mark a view as having been rendered. This is called from inside
		 * views' render() methods. */
        markRendered: function () {
            // mark it rendered
            this.$el.attr("data-rendered", true);
        },

        /** determine if a view has been marked as already rendered. This is
		 * often called from inside views' render() methods to avoid rendering
		 * twice.
		 * @returns {Boolean} rendered, or not? */
        alreadyRendered: function () {
            return this.$el.attr("data-rendered") === "true";
        },

        /** returns a truth value indicating whether or not the view is
		 * "visible" by jQuery's standards
		 * (http://api.jquery.com/visible-selector/). This is NOT tell you
		 * whether or not an element has been scrolled out of view.
		 * @returns {Boolean} whether or not the view is visible
		 */
        isVisible: function () {
            return !!this.$el.filter(":visible").length;
        }
    });

    /**
	 * For displaying the report selector on the landing page
	 * @class module:cardboard/baseViews.ReportListView
	 * @augments BaseViews.BaseView
	 * @classdesc View to display the list of reports/topics to pick from.
	 * @param {Object} options a hash of options to pass to the
	 * backbone view. {@link http://backbonejs.org/#View-constructor}
	 */
    BaseViews.ReportListView = BaseViews.BaseView.extend(
        /** @lends module:cardboard/baseViews.ReportListView.prototype */
        {
            // properties

            /** the element */
            el: null,

            /** the id */
            id: null,

            /** the model we're attached to */
            model: null,

            /** wrapper element template
		 * @type String */
            wrapperTemplate: "<div class='reportListView'></div>",

            reportListTemplate: reportListTemplate,

            /** item highlight class */
            // itemHighClass: 'selected',
            itemHighClass: "selected",

            /** declarative events */
            events: {
                "click li a": "onReportTap"
            },

            /** custom declarative events */
            globalEvents: {
                "beforeWorkspaceStateChange": "onBeforeWorkspaceStateChange",
                "workspaceStateChangeFailed": "onWorkspaceStateChangeFailed",
                "introShown": "onIntroShown"
            },

            /**
		 * constructor.
		 * @param {Object} options a hash of options to pass to the
		 * backbone view. {@link http://backbonejs.org/#View-constructor}
		 */
            initialize: function (options) {
                this.options = options;

                // routine view setup
                this.createWrapperElement();
                this.showProgress();
                this.container = options.container;

                // bind to model's change event
                this.listenTo(this.model, "sync", this.render);
                this.attachGlobalEvents();
            },

            // info methods

            /**
		 * find a report's tile in the report list
		 * @param {Backbone.Model | Object} tapInfoOrModel - report model or
		 * informal tileInfo object
		 * @returns {jQuery} the requested report tile; null if not found
		 */
            findTile: function (tileInfoOrModel) {
                var $el,
                    tileInfo = tileInfoOrModel;

                if (tileInfoOrModel instanceof Backbone.Model) {
                    tileInfo = {
                        reportModel: tileInfoOrModel
                    };
                }

                if (tileInfo.reportModel) {
                    $el = $(this.tileFromReportModel(tileInfo.reportModel));

                    // if (_.isString(report)) {
                    // 	// it's a report code
                    // 	$el = $(this.tileFromReportCode(report));
                    // } else if (_.isNumber(report)) {
                    // 	// it's a report ID
                    // 	$el = $(this.tileFromReportID(report));
                    // } else {
                    // 	// it's a report Model object
                    // 	$el = $(this.tileFromReportModel(report));
                    // }
                } else if (tileInfo.pageID) {
                    $el = $(this.tileFromPageID(tileInfo.pageID));
                } else if (tileInfo.pageURL) {
                    $el = $(this.tileFromPageURL(tileInfo.pageURL));
                }

                return $el;
            },

            /**
		 * get the list item tile DOM element, given a report object.
		 * @param {Object} report
		 * @returns {DOMElement}
		 */
            tileFromReportModel: function (reportModel) {
                var id = reportModel.get("code");
                return this.tileFromReportID(id);
            },

            /**
		 * get the list item tile DOM element, given a numeric report id.
		 * @param {Number} id
		 * @returns {DOMElement}
		 */
            tileFromReportID: function (id) {
                var $el = $("#reportTile_" + id);
                if ($el.length) { return $el[0]; }
                return null;
            },

            /**
		 * get the list item tile DOM element, given a string report code.
		 * @param {String} code
		 * @returns {DOMElement}
		 */
            tileFromReportCode: function (code) {
                var $el = this.$("[data-reportcode = '" + code + "']");
                if ($el.length) { return $el[0]; }
                return null;
            },

            /**
		 * get the list item tile DOM element, given the client-side URL it
		 * points to
		 * @param {String} url
		 * @returns {DOMElement}
		 */
            tileFromPageURL: function (url) {
                var $el = this.$("[href = '" + url + "']").closest("li");
                if ($el.length) { return $el[0]; }
                return null;
            },

            /**
		 * get the list item tile DOM element, given the client-side page ID it
		 * asks for
		 * @param {String} pageID
		 * @returns {DOMElement}
		 */
            tileFromPageID: function (pageID) {
                var $el = this.$("[data-pageID = '" + pageID + "']").closest("li");
                if ($el.length) { return $el[0]; }
                return null;
            },

            // actions

            /** render the view */
            render: function () {
                if (this.alreadyRendered()) { return false; }
                if (this.model.length === 0) { return false; }

                // render with Hogan and slam it into the DOM
                var $ul = $(Hogan
                            .compile(this.reportListTemplate)
                            .render(this.getPreparedReportsList())
                           );
                this.$el.html($ul);
                $(this.container).html(this.$el);
                $ul.listview();

                // make the debug toolbar
                this.createDebugToolbar();

                this.markRendered();

                return true;
            },

            getPreparedReportsList: function () {
                var viewModel = this.model.toJSON();

                // go over every report and mark it as either having or
                // not having an implemented plugin
                $(viewModel.reports).each(function (index, report) {
                    report.hasPlugin = PluginHelper.pluginExists(report.url);
                });

                viewModel.reports = _(viewModel.reports)
                // reject if we don't want to show reports without plugins
                // .reject(function (report) { return !report.hasPlugin; })
                    .reverse()
                    .sortBy("sort")
                    .value();

                return viewModel;
            },

            createDebugToolbar: function () {
                if (window.debugEnabled) {
                    var buttons = {
                        "Report List JSON": function () { return this.model; }.bind(this)
                    };
                    this.$el.prepend(new BaseViews.DebugToolbar(buttons).$el);
                }
            },

            /**
		 * highlight a single report tile, dimming any others
		 * @param {Object | String} report model or ID
		 */
            highlight: function (newWorkspaceState) {
                var $el = this.findTile(newWorkspaceState);

                if (!newWorkspaceState || !$el || !$el.length) {
                    this.dimCurrent();
                    return;
                }

                // already highlighted?
                if (!$el.is(".selected")) {
                    this.dimCurrent();
                    $el.addClass(this.itemHighClass);
                }
            },

            /**
		 * dim a single report tile
		 * @param {Element | jQuery} report
		 */
            dim: function (reportLI) {
                var $reportLI = $(reportLI),
                    $a = $reportLI.find("a");

                // group DOM writes to avoid layout thrashing
                $reportLI.removeClass(this.itemHighClass);
                $a.removeClass("ui-btn-active");
            },

            /** dim the currently highlighted report tile */
            dimCurrent: function () {
                this.dim($(this.container).find("ul>li." + this.itemHighClass));
            },

            /** set keyboard focus to the first report in the list */
            focusFirstReportTile: function () {
            
           //    this.$("ul a").first().focus();
           
            },

            // event handlers

            /** browser event handler: when a report is tapped
		 * @fires reportTapped */
            onReportTap: function (e) {
                if($(e.target).closest("ul").hasClass('cms-tools')){
                    return;
                }
                e.preventDefault();
                e.stopImmediatePropagation(); // added to prevent default link
                // behavior on the information group

                var $link = $(e.target).closest("a"),
                    questionID = $link.attr("data-qid"),
                    pageURL = $link.attr("href"),
                    pageID = $link.attr("data-pageID"),
                    reportModel = this.model.findReport(questionID),
                    tapInfo = {
                        reportModel: reportModel,
                        pageURL: pageURL,
                        pageID: pageID
                    };

                this.highlight(tapInfo);

                // give the highlight some time to draw
                // setTimeout(function () {
                // 	Backbone.trigger("reportTapped", tapInfo);
                // }, 10);
                Backbone.trigger("reportTapped", tapInfo);
            },
            /** custom event handler: when a report has been set, we must react
		 * to reflect that*/
            onBeforeWorkspaceStateChange: function (newState) {
                this.highlight(newState);
            },
            /** custom event handler: restore the correct highlight when a
		 * report set attempt fails */
            onWorkspaceStateChangeFailed: function (workingState) {
                this.highlight(workingState);
            },
            /** custom event handler: un-highlight the currently highlighted report
		 * if the intro is suddenly shown - but only if there's no intro tile
		 * anywhere in the navigation. */
            onIntroShown: function () {
                var introTile = this.findTile({pageID: "intro"}) || this.findTile({pageURL: "/"});
                if (!introTile) {
                    this.dimCurrent();
                }
            }

        });

    /**
	 * For displaying the report summary text and info button on the main
	 * workspace.
	 * @class module:cardboard/baseViews.QuestionBarView
	 * @augments BaseViews.BaseView
	 * @param {Object} options a hash of options to pass to the
	 * backbone view. {@link http://backbonejs.org/#View-constructor}
	 */
    BaseViews.QuestionBarView = BaseViews.BaseView.extend(
        /** @lends module:cardboard/baseViews.QuestionBarView.prototype */
        {
            // properties

            /** the element */
            el: null,

            /** the model we're attached to */
            model: null,

            /** declarative events */
            events: {
               "click a": "onInfoButtonTap",
               "click": "onInfoButtonTap"
            },

            /** custom declarative events */
            globalEvents: {
                "beforeReportSet": "onReportSet"
            },

            /**
		 * constructor.
		 * @param {Object} options a hash of options to pass to the
		 * backbone view. {@link http://backbonejs.org/#View-constructor}
		 */
            initialize: function (options) {
                this.options = options;

                this.attachGlobalEvents();
            },

            // actions

            focusInfoButton: function () {
                this.$("a").first().focus();
            },
            update: function (reportModel) {
                this.$(".name").html(reportModel.getDisplayName() + ":");
                this.$(".description").html(reportModel.getShortDescription());
            },

            // event handlers

            /** browser event handler: when a report is tapped
		 * @fires reportTapped 
		 * @param {object} the event fired
		 */
            onInfoButtonTap: function (e) {
                console.log(window.selectedLang);
                var reportModel = window.workspace.getCurrentPluginModel(),
                    desc = reportModel.get("description");

                if (location.host.indexOf("eclds") < 0){
                 
                    // scrub the CDATA and stuff out
                    desc = desc.replace("<![CDATA[", "");
                    desc = desc.replace("]]>", "");
                    desc = "<h3>" + reportModel.getDisplayName() + ": " + reportModel.getShortDescription() + "</h3>" + desc ;

                    window.workspace.setModalInfoDialogContent(desc);

                    // this.$popup.popup("open");
                    window.workspace.showModalInfoDialog();
                    if (location.host.indexOf("rc") == 0){
                    // Translate the information dialog

                    setTimeout(function () {
                        if (window.selectedLang != 'en') {
                            changeLang(window.selectedLang);
                        }
                    }, 1500);
                    }

                }
    
                if(e){
                    e.preventDefault();
                }
            },
            onReportSet: function (reportModel) {
                this.update(reportModel);
            }
        });

    /**
	 * A View based on Mustache templates
	 * @class module:cardboard/baseViews.TemplateView
	 * @augments BaseViews.BaseView
	 * @classdesc Can be used to display any result set, really.
	 * @param {Object} options a hash of options to pass to the
	 * backbone view. {@link http://backbonejs.org/#View-constructor}
	 */

    BaseViews.TemplateView = BaseViews.BaseView.extend(/** @lends module:cardboard/baseViews.TemplateView.prototype */{
        /** wrapper element template
		 * @type String */
        wrapperTemplate: "<div class='view templateView'></div>",

        /** constructor
		 * @param {Object} options a hash of options to pass to the
		 * backbone view. {@link http://backbonejs.org/#View-constructor} */
        initialize: function (options) {
            this.options = options;
            this.options.viewInfo = this.preProcessViewInfo(this.options.viewInfo);
            this.createWrapperElement(options);
            if(this.options.viewInfo.templatePath){
                this.template = Hogan.compile(this.getDynamicContent( this.options.viewInfo.templatePath ));
                this.$el.attr('data-dynamickey', this.options.viewInfo.templatePath);
            } else {
                this.template = Hogan.compile(this.options.viewInfo.template);				
            }
            this.showProgress();

            // listen for events on the model only if there is one
            if (this.model) {
                this.listenTo(this.model, "sync", this.render.bind(this));
                this.listenTo(this.model, "request", this.maskForLoading);
            }
        },

        _getData: function () {
            var dataView = (this.model ?
                            this.model.getCleanDataView(this.options.viewInfo) :
                            null);
       
            return dataView;
        },

        render: function () {
            if (this.model && this.model.isLoaded() || !this.model) {
                this.unmaskForLoading();
                this.$el.html(this.template.render(this._getData()));
                this.applyVisibility();
            }
          
        }
    });

    /**
	 * A debug toolbar that can be put anywhere
	 * @class module:cardboard/baseViews.DebugToolbar
	 * @augments BaseViews.BaseView
	 * @classdesc A debug toolbar that can be put anywhere
	 * @param {Object} options a hash of options to pass to the
	 * backbone view. {@link http://backbonejs.org/#View-constructor}
	 */

    BaseViews.DebugToolbar = BaseViews.BaseView.extend(/** @lends module:cardboard/baseViews.DebugToolbar.prototype */{
        events: {
            "click a": "onButtonClick"
        },

        /** wrapper element template
		 * @type String */
        wrapperTemplate: "<div class='debugToolbar'>"
        + "<h5>debug bar</h5>"
        + (window.fastOrgs ? "<div class='warning'>Fast Orgs enabled!</div>" : "")
        + (!Modernizr.csstransforms3d ? "<div class='warning'>No CSS 3D transforms!</div>" : "")
        + "</div>",
        buttonTemplate: Hogan.compile("{{#buttons}}<a data-blob='{{blob}}' data-action='{{action}}' href='#'>"
                                      + "{{label}}"
                                      + "</a>{{/buttons}}"),

        /** constructor
		 * @param {Object} options a hash of options to pass to the
		 * backbone view. {@link http://backbonejs.org/#View-constructor} */
        initialize: function (buttons) {
            this.buttons = buttons;

            this.createWrapperElement(buttons);
            this.render();
        },

        destroy: function () {
            this.$el.remove();
            this.buttons = null;
        },

        render: function () {
            var viewModel = { buttons: [] };

            _(this.buttons).each(function (value, key) {
                viewModel.buttons.push({ label: "View " + key, blob: key });
            }.bind(this));

            this.$el.append($(this.buttonTemplate.render(viewModel)));
        },

        // ACTIONS

        onButtonClick: function (event) {
            var $button = $(event.target),
                blobName = $button.attr("data-blob"),
                data = this.buttons[blobName];

            if (_.isFunction(data)) { data = data(); }

            if (_.isString(data)) {
                window.open(data, blobName);
            } else if (_.isObject(data)) {
                Util.inspectJSON(data, blobName);
            }
        }
    });

    /**
	 * The panel used to add a new databox for comparison.
	 * @class module:cardboard/baseViews.DataBoxAdder
	 * @augments BaseViews.BaseView
	 * @classdesc The panel used to add a new databox for comparison.
	 * @param {Object} options a hash of options to pass to the
	 * backbone view. {@link http://backbonejs.org/#View-constructor}
	 */

    BaseViews.DataBoxAdder = BaseViews.BaseView.extend(/** @lends module:cardboard/baseViews.DataBoxAdder.prototype */{
        events: {
            "click .addButton": "onAddButtonClick"
        },

        /** wrapper element template
		 * @type String */
        wrapperTemplate: dataBoxAdderTemplate,

        /** constructor
		 * @param {Object} options a hash of options to pass to the
		 * backbone view. {@link http://backbonejs.org/#View-constructor} */
        initialize: function (options) {
            this.options = options;
            this.createWrapperElement(options);
            this.$container = $("#dataBoxes");
            this.render();
        },

        render: function () {
            // prepare the data to be sent to the Mustache template
            this.$container.append(this.el);
        },

        // ACTIONS

        onAddButtonClick: function (event) {
            event.preventDefault();
            this.trigger("addDataBox");
        }
    });

    /**
	 * Singleton used for putting up the yellow help bubbles.
	 * @class module:cardboard/baseViews.BubbleMaker
	 * @augments BaseViews.BaseView
	 * @classdesc Singleton used for putting up the yellow help bubbles.
	 * @param {Object} options a hash of options to pass to the backbone
	 * view. {@link http://backbonejs.org/#View-constructor}
	 */
    BaseViews.BubbleMaker = BaseViews.BaseView.extend(/** @lends module:cardboard/baseViews.BubbleMaker.prototype */{
        wrapperTemplate: "<div class='bubble hidden'>"
        + "<button class='close'><span>X</span></button>"
        + "<div class='message'></div>"
        + "</div>",
        events: {
            "click button.close": "onCloseButtonClicked",
            "click": "onCloseButtonClicked"
        },
        globalEvents: {
            "bubblesShouldClose": "hideImmediately"
        },

        initialize: function (options) {
            this.options = options;

            var $parentElement = $(document.body);
            this.createWrapperElement();
            this.attachGlobalEvents();

            this.positionOffscreen();
            $parentElement.append(this.$el);
        },

        show: function (optionsIn) {
            if (settings.get("helpBubbles") === "off") { return; }

            var options = this.options = $.extend({
                direction: "up",		// the direction of the arrow
                pointingAt: null,		// the element to point at
                align: "left",			// which part to point at
                message: "",			// some html to put in the bubble
                duration: 7,			// seconds to remain, null means forever
                fadeDuration: 700,		// fade duration in ms
                pulsateElement: true,	// call attention to the indicated element
                nudge: {				// change the default location of the bubble, relative to its target
                    x: 0,
                    y: 0
                }
            }, optionsIn);

            // this.options.pointingAt = $(this.options.pointingAt);

            // render the message, set pointer direction and preset the opacity
            this.setMessage(options.message);
            this.setDirection(options.direction);
            // this.zeroOpacity();

            // position it
            this.position();

            // fade it in
            this.fadeIn(options);

            // make it glow
            if (options.pointingAt && options.pulsateElement) {
                $(options.pointingAt).addClass("pulsating");
            }

            // set timer to hide it
            if (options.duration) {
                this.closeTimer = setTimeout(this.hide.bind(this), options.duration * 1000);
            }
        },
        hide: function () {
            if (!this.options) { return; }

            clearTimeout(this.closeTimer);
            // this.$el.animate({"opacity": 0}, this.hideImmediately.bind(this));
            this.zeroOpacity();
            setTimeout(this.hideImmediately.bind(this), 800);
        },
        hideImmediately: function () {
            if (!this.options) { return; }

            var el = this.options.pointingAt;
            clearTimeout(this.closeTimer);
            // this.positionOffscreen();
            this.zeroOpacity();
            this.$el.hide();
            if (el) { $(el).removeClass("pulsating"); }
        },

        // utility & internal methods
        position: function () {
            var $target = $(this.options.pointingAt),
                targetOffset = $target.offset();

            if (this.options.direction === "up") {
                this.$el.css({
                    top: (targetOffset.top +
                          $target.outerHeight() +
                          this.options.nudge.y +
                          0) + "px",
                    left: targetOffset.left + this.options.nudge.x + "px",
                    right: "auto",
                    bottom: "auto"
                });
            } else if (this.options.direction === "down") {
                this.$el.css({
                    top: (targetOffset.top -
                          this.$el.outerHeight() +
                          this.options.nudge.y -
                          22) + "px",
                    left: targetOffset.left + this.options.nudge.x + "px",
                    right: "auto",
                    bottom: "auto"
                });
            }
        },
        positionOffscreen: function () {
            this.$el.css({
                left: "-9999px",
                position: "absolute"
            });
        },
        zeroOpacity: function () {
            // this.$el.css("opacity", 0);
            this.$el.addClass("hidden");
        },
        fadeIn: function () {
            // this.$el.animate({opacity: 1}, this.options.fadeDuration);
            this.$el.removeClass("hidden");
        },
        setMessage: function (message) {
            this.$(".message").html(message);
        },
        setDirection: function (direction) {
            this.$el.removeClass("up down left right").addClass(direction);
        },

        // event handlers
        onCloseButtonClicked: function (event) {
            event.preventDefault();
            event.stopPropagation();
            this.hide();
        }
    });

    return BaseViews;
});

